import {
  createContext,
  use,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type {
  GridAggregationModel,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridRowGroupingModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import type { ColumnFiltersState } from "@tanstack/react-table";

type TableState = {
  filterModel?: GridFilterModel;
  sortModel?: GridSortModel;
  paginationModel: GridPaginationModel;
  groupingModel?: GridRowGroupingModel;
  aggregationModel?: GridAggregationModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnOrderModel?: string[];
  columnFilter?: ColumnFiltersState;
  columnWidthModel?: Record<string, number>;
};

export type FilterType = {
  columnFilter: ColumnFiltersState | undefined | null;
  setColumnFilter: Dispatch<
    SetStateAction<ColumnFiltersState | undefined | null>
  >;
  tablesState: TablesState;
  searchText: string;
  setTableState: (tableKey: string, newState: Partial<TableState>) => void;
  setSearchText: (text: string) => void;
  resetAllTables: () => void;
};

type TablesState = Record<string, TableState>;

const FilterContext = createContext<FilterType | undefined>(undefined);

type FilterProviderProps = { children: React.ReactNode };

const FilterContextProvider = ({ children }: FilterProviderProps) => {
  const [columnFilter, setColumnFilter] = useState<
    ColumnFiltersState | undefined | null
  >();

  const localStorageState: TablesState = (() => {
    try {
      const storedState = localStorage.getItem("tableState");
      if (storedState) {
        const parsed = JSON.parse(storedState) as unknown;
        if (typeof parsed === "object" && parsed !== null) {
          return parsed as TablesState;
        }
      }
    } catch {
      /* empty */
    }
    return {};
  })();

  const [tablesState, setTablesState] =
    useState<TablesState>(localStorageState);

  const [searchText, setSearchText] = useState("");

  const setTableState = (tableKey: string, newState: Partial<TableState>) => {
    setTablesState((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        ...newState,
      },
    }));
  };

  const resetAllTables = () => {
    setTablesState({});
    setColumnFilter(undefined);
    setSearchText("");
  };

  useEffect(() => {
    localStorage.setItem("tableState", JSON.stringify(tablesState));
  }, [tablesState]);

  return (
    <FilterContext.Provider
      value={{
        tablesState,
        setTableState,
        setColumnFilter,
        columnFilter,
        setSearchText,
        searchText,
        resetAllTables,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

function useFilterContext() {
  const context = use(FilterContext);
  if (context === undefined) {
    throw new Error("useFilterContext must be used within a FilterProvider");
  }
  return context;
}

export { FilterContextProvider, useFilterContext };
